
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { db, auth, checkOnlineStatus } from '@/services/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { User, Mail, Phone, CreditCard, Check, Building, Banknote, AlertTriangle, RefreshCw, Wifi, WifiOff } from 'lucide-react';

const MyProfile = () => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, loading, authError] = useAuthState(auth);
  const [profileLoading, setProfileLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    referrerCode: '',
    paymentMethod: 'check',
    payableTo: '',
    mailingAddress: '',
    accountType: 'personal',
    bankName: '',
    accountNumber: '',
    routingNumber: ''
  });
  
  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log("Browser is online");
      setIsOnline(true);
      if (open) {
        setRetryCount(prev => prev + 1); // Trigger a retry when we come back online
      }
    };
    
    const handleOffline = () => {
      console.log("Browser is offline");
      setIsOnline(false);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [open]);
  
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user || !open) return;
      
      try {
        setProfileLoading(true);
        setError(null);
        console.log("Fetching user data for: ", user.uid);
        
        // Check online status first
        if (!navigator.onLine) {
          console.warn("Browser reports offline status");
          setError("You appear to be offline. Please check your internet connection and try again.");
          
          // Still set the email from Auth state
          if (user.email) {
            setProfileData(prev => ({
              ...prev,
              email: user.email || ''
            }));
          }
          
          setProfileLoading(false);
          return;
        }
        
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          console.log("User document found:", userDoc.data());
          const userData = userDoc.data();
          setProfileData(prev => ({
            ...prev,
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            email: userData.email || user.email || '',
            phone: userData.phone || '',
            referrerCode: userData.referrerCode || '',
            paymentMethod: userData.paymentMethod || 'check',
            payableTo: userData.payableTo || '',
            mailingAddress: userData.mailingAddress || '',
            accountType: userData.accountType || 'personal',
            bankName: userData.bankName || '',
            accountNumber: userData.accountNumber || '',
            routingNumber: userData.routingNumber || ''
          }));
        } else {
          console.log("No user document found for:", user.uid);
          // Default to auth email at minimum
          setProfileData(prev => ({
            ...prev,
            email: user.email || '',
            referrerCode: localStorage.getItem('referrerCode') || '' // Try to get from localStorage
          }));
          setError("Your profile information is incomplete. Please update your details.");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        
        // Check if this is an offline error
        if (error.message && (
            error.message.includes("offline") || 
            error.message.includes("network") || 
            error.message.includes("connect")
          )) {
          setError("Unable to connect to the server. Please check your internet connection and try again.");
        } else {
          setError("Failed to load profile data. Please try again.");
        }
        
        // Still set the email from Auth state
        if (user && user.email) {
          setProfileData(prev => ({
            ...prev,
            email: user.email || '',
            referrerCode: localStorage.getItem('referrerCode') || '' // Try to get from localStorage
          }));
        }
      } finally {
        setProfileLoading(false);
      }
    };
    
    fetchUserData();
  }, [user, open, retryCount]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleRadioChange = (name: string, value: string) => {
    setProfileData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleRetry = () => {
    if (!navigator.onLine) {
      toast.error("You are offline. Please check your internet connection.");
      return;
    }
    
    setRetryCount(prev => prev + 1);
    toast.info("Retrying to load profile data...");
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Not logged in");
      return;
    }
    
    if (!navigator.onLine) {
      toast.error("You are offline. Please check your internet connection before saving changes.");
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Update user document in Firestore
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        phone: profileData.phone,
        paymentMethod: profileData.paymentMethod,
        payableTo: profileData.payableTo,
        mailingAddress: profileData.mailingAddress,
        accountType: profileData.accountType,
        bankName: profileData.bankName,
        accountNumber: profileData.accountNumber,
        routingNumber: profileData.routingNumber
      });
      
      setOpen(false);
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Update profile error:", error);
      
      // Check if this is an offline error
      if (error.message && (
          error.message.includes("offline") || 
          error.message.includes("network") || 
          error.message.includes("connect")
        )) {
        toast.error("Unable to connect to the server. Please check your internet connection and try again.");
      } else {
        toast.error("Failed to update profile. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Show online/offline status indicator
  const NetworkStatus = () => (
    <div className={`flex items-center justify-center space-x-2 mb-2 ${isOnline ? 'text-green-500' : 'text-red-500'}`}>
      {isOnline ? (
        <>
          <Wifi className="h-4 w-4" />
          <span className="text-sm font-medium">Online</span>
        </>
      ) : (
        <>
          <WifiOff className="h-4 w-4" />
          <span className="text-sm font-medium">Offline</span>
        </>
      )}
    </div>
  );
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="glass-button flex items-center space-x-2">
          <User className="mr-2 h-4 w-4" />
          <span>My Profile</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="glass-panel sm:max-w-[500px] animate-fadeIn max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">My Profile</DialogTitle>
          <DialogDescription className="text-center text-base">
            View and update your personal information
          </DialogDescription>
        </DialogHeader>
        
        <NetworkStatus />
        
        {profileLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-pulse text-xl">Loading profile...</div>
          </div>
        ) : error ? (
          <div className="text-center py-4">
            <div className="flex items-center justify-center space-x-2 text-amber-500 mb-2">
              <AlertTriangle className="h-5 w-5" />
              <p className="font-medium">{error}</p>
            </div>
            
            <Button 
              onClick={handleRetry} 
              variant="outline" 
              className="mb-4 flex items-center"
              disabled={!isOnline}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
            
            {!isOnline ? (
              <p className="text-sm text-muted-foreground mb-4">
                You are currently offline. Please check your internet connection and try again.
              </p>
            ) : null}
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="flex items-center">
                    <User className="mr-2 h-4 w-4" />
                    First Name
                  </Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    required
                    className="input-style"
                    value={profileData.firstName}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="flex items-center">
                    <User className="mr-2 h-4 w-4" />
                    Last Name
                  </Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    required
                    className="input-style"
                    value={profileData.lastName}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center">
                  <Mail className="mr-2 h-4 w-4" />
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="input-style"
                  value={profileData.email}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center">
                  <Phone className="mr-2 h-4 w-4" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  required
                  className="input-style"
                  value={profileData.phone}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="referrerCode" className="flex items-center">
                  <CreditCard className="mr-2 h-4 w-4" />
                  Your Referrer Code
                </Label>
                <Input
                  id="referrerCode"
                  name="referrerCode"
                  readOnly
                  className="input-style bg-muted"
                  value={profileData.referrerCode}
                />
                <p className="text-xs text-muted-foreground">
                  This is your unique referrer code that cannot be changed
                </p>
              </div>
              
              <div className="space-y-4 pt-2 border-t border-border">
                <h3 className="font-semibold text-lg flex items-center">
                  <Banknote className="mr-2 h-5 w-5" />
                  Payment Preferences
                </h3>
                
                <RadioGroup 
                  value={profileData.paymentMethod} 
                  onValueChange={(value) => handleRadioChange('paymentMethod', value)}
                  className="space-y-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="check" id="payment-check" />
                    <Label htmlFor="payment-check" className="flex items-center">
                      <Check className="mr-2 h-4 w-4" />
                      Check
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="bank" id="payment-bank" />
                    <Label htmlFor="payment-bank" className="flex items-center">
                      <Building className="mr-2 h-4 w-4" />
                      Bank Transfer
                    </Label>
                  </div>
                </RadioGroup>
                
                {profileData.paymentMethod === 'check' ? (
                  <div className="space-y-4 pl-6">
                    <div className="space-y-2">
                      <Label htmlFor="payableTo">Make Payable To</Label>
                      <Input
                        id="payableTo"
                        name="payableTo"
                        className="input-style"
                        value={profileData.payableTo}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mailingAddress">Mailing Address</Label>
                      <Input
                        id="mailingAddress"
                        name="mailingAddress"
                        className="input-style"
                        value={profileData.mailingAddress}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 pl-6">
                    <RadioGroup 
                      value={profileData.accountType} 
                      onValueChange={(value) => handleRadioChange('accountType', value)}
                      className="space-y-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="personal" id="account-personal" />
                        <Label htmlFor="account-personal">Personal Checking</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="business" id="account-business" />
                        <Label htmlFor="account-business">Business Checking</Label>
                      </div>
                    </RadioGroup>
                    
                    <div className="space-y-2">
                      <Label htmlFor="bankName">Bank Name</Label>
                      <Input
                        id="bankName"
                        name="bankName"
                        className="input-style"
                        value={profileData.bankName}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="accountNumber">Account Number</Label>
                      <Input
                        id="accountNumber"
                        name="accountNumber"
                        className="input-style"
                        value={profileData.accountNumber}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="routingNumber">Routing Number</Label>
                      <Input
                        id="routingNumber"
                        name="routingNumber"
                        className="input-style"
                        value={profileData.routingNumber}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end space-x-3 pt-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setOpen(false)}
                  className="border-accent text-accent hover:bg-accent/10"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="glass-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="flex items-center">
                  <User className="mr-2 h-4 w-4" />
                  First Name
                </Label>
                <Input
                  id="firstName"
                  name="firstName"
                  required
                  className="input-style"
                  value={profileData.firstName}
                  onChange={handleInputChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="flex items-center">
                  <User className="mr-2 h-4 w-4" />
                  Last Name
                </Label>
                <Input
                  id="lastName"
                  name="lastName"
                  required
                  className="input-style"
                  value={profileData.lastName}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center">
                <Mail className="mr-2 h-4 w-4" />
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                className="input-style"
                value={profileData.email}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center">
                <Phone className="mr-2 h-4 w-4" />
                Phone Number
              </Label>
              <Input
                id="phone"
                name="phone"
                required
                className="input-style"
                value={profileData.phone}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="referrerCode" className="flex items-center">
                <CreditCard className="mr-2 h-4 w-4" />
                Your Referrer Code
              </Label>
              <Input
                id="referrerCode"
                name="referrerCode"
                readOnly
                className="input-style bg-muted"
                value={profileData.referrerCode}
              />
              <p className="text-xs text-muted-foreground">
                This is your unique referrer code that cannot be changed
              </p>
            </div>
            
            <div className="space-y-4 pt-2 border-t border-border">
              <h3 className="font-semibold text-lg flex items-center">
                <Banknote className="mr-2 h-5 w-5" />
                Payment Preferences
              </h3>
              
              <RadioGroup 
                value={profileData.paymentMethod} 
                onValueChange={(value) => handleRadioChange('paymentMethod', value)}
                className="space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="check" id="payment-check" />
                  <Label htmlFor="payment-check" className="flex items-center">
                    <Check className="mr-2 h-4 w-4" />
                    Check
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="bank" id="payment-bank" />
                  <Label htmlFor="payment-bank" className="flex items-center">
                    <Building className="mr-2 h-4 w-4" />
                    Bank Transfer
                  </Label>
                </div>
              </RadioGroup>
              
              {profileData.paymentMethod === 'check' ? (
                <div className="space-y-4 pl-6">
                  <div className="space-y-2">
                    <Label htmlFor="payableTo">Make Payable To</Label>
                    <Input
                      id="payableTo"
                      name="payableTo"
                      className="input-style"
                      value={profileData.payableTo}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mailingAddress">Mailing Address</Label>
                    <Input
                      id="mailingAddress"
                      name="mailingAddress"
                      className="input-style"
                      value={profileData.mailingAddress}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4 pl-6">
                  <RadioGroup 
                    value={profileData.accountType} 
                    onValueChange={(value) => handleRadioChange('accountType', value)}
                    className="space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="personal" id="account-personal" />
                      <Label htmlFor="account-personal">Personal Checking</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="business" id="account-business" />
                      <Label htmlFor="account-business">Business Checking</Label>
                    </div>
                  </RadioGroup>
                  
                  <div className="space-y-2">
                    <Label htmlFor="bankName">Bank Name</Label>
                    <Input
                      id="bankName"
                      name="bankName"
                      className="input-style"
                      value={profileData.bankName}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="accountNumber">Account Number</Label>
                    <Input
                      id="accountNumber"
                      name="accountNumber"
                      className="input-style"
                      value={profileData.accountNumber}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="routingNumber">Routing Number</Label>
                    <Input
                      id="routingNumber"
                      name="routingNumber"
                      className="input-style"
                      value={profileData.routingNumber}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3 pt-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setOpen(false)}
                className="border-accent text-accent hover:bg-accent/10"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="glass-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MyProfile;
